import type {
  AttendanceOverview,
  PayrollOverview,
  EmployeesOverview,
  ProjectsOverview,
} from './types';

// KPIMetric type - inline to avoid circular dependency with components
// This matches the prop shape expected by KPICard.tsx
export type KPIMetric = {
  id: string;
  title: string;
  value: string;
  /** Optional: only present when a real prior-period comparison is available */
  change?: string;
  trend?: 'up' | 'down';
  /** True when `change` is informational (e.g. net hires−exits) and not a vs-previous delta */
  isNeutralChange?: boolean;
  icon: string;
  color: 'blue' | 'green' | 'orange' | 'purple' | 'red';
  category: 'attendance' | 'payroll' | 'employee' | 'project';
  description: string;
};

// HolidayCard prop shape
export type HolidayCardData = {
  id: string;
  name: string;
  date: string;
  daysUntil: number;
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format a decimal value as a percentage string.
 * Returns "0%" if value is null/undefined.
 */
export function formatPercentage(value: number | null | undefined): string {
  if (value == null) return '0%';
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Calculate percentage change between current and previous values.
 * Returns null when no real comparison is possible.
 */
export function formatChange(
  current: number,
  previous: number | undefined
): { change: string; trend: 'up' | 'down' } | null {
  if (previous == null || previous === 0) return null;
  const delta = ((current - previous) / previous) * 100;
  const isUp = delta >= 0;
  const sign = isUp ? '+' : '';
  return {
    change: `${sign}${delta.toFixed(1)}%`,
    trend: isUp ? 'up' : 'down',
  };
}

/**
 * Format a currency value with the currency label.
 */
function formatCurrency(value: number | null | undefined, currency: string): string {
  if (value == null) return `${currency}0`;
  const formatted = value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `${currency}${formatted}`;
}

/**
 * Compute change between first and last entry in a trend series.
 * Returns null when fewer than 2 points or any value is missing.
 */
function getChangeFromTrend<T extends { [key: string]: unknown }>(
  trend: T[] | undefined,
  valueKey: keyof T
): { change: string; trend: 'up' | 'down' } | null {
  if (!trend || trend.length < 2) return null;
  const first = trend[0][valueKey] as number | undefined;
  const last = trend[trend.length - 1][valueKey] as number | undefined;
  if (first == null || last == null) return null;
  return formatChange(last, first);
}

// =============================================================================
// Attendance Mapping
// =============================================================================

export function mapAttendanceToKPIs(
  data: AttendanceOverview,
  t: (key: string, fallback?: string) => string
): { primary: KPIMetric; secondary: KPIMetric[] } {
  const presentChange = getChangeFromTrend(data.trend, 'presentRate');
  const lateChange = getChangeFromTrend(data.trend, 'lateRate');

  const primary: KPIMetric = {
    id: 'primary-attendance',
    title: t('attendance.present', 'Present %'),
    value: formatPercentage(data.presentRate),
    ...(presentChange ?? {}),
    icon: 'CheckCircle',
    color: 'green',
    category: 'attendance',
    description: t(
      'attendance.present_tooltip',
      'Share of expected workdays with recorded clock-in'
    ),
  };

  const secondary: KPIMetric[] = [
    {
      id: 'att-late',
      title: t('attendance.late', 'Late %'),
      value: formatPercentage(data.lateRate),
      // For late %, down is good — invert the trend indicator
      ...(lateChange
        ? { change: lateChange.change, trend: lateChange.trend === 'up' ? 'down' as const : 'up' as const }
        : {}),
      icon: 'Clock',
      color: 'orange',
      category: 'attendance',
      description: t(
        'attendance.late_tooltip',
        'Fraction of shifts clocked-in later than scheduled'
      ),
    },
    {
      id: 'att-hours',
      title: t('attendance.avg_hours', 'Avg Hours per Shift'),
      value: `${(data.avgHours ?? 0).toFixed(1)} ${t('hrs')}`,
      icon: 'Timer',
      color: 'blue',
      category: 'attendance',
      description: t(
        'attendance.avg_hours_tooltip',
        'Average worked hours per completed shift'
      ),
    },
    {
      id: 'att-overtime',
      title: t('attendance.overtime', 'Overtime'),
      value: `${(data.overtimeHours ?? 0).toFixed(0)} ${t('hrs')}`,
      icon: 'TrendingUp',
      color: 'purple',
      category: 'attendance',
      description: t(
        'attendance.overtime_tooltip',
        'Total overtime hours worked above scheduled time'
      ),
    },
  ];

  return { primary, secondary };
}

// =============================================================================
// Payroll Mapping
// =============================================================================

export function mapPayrollToKPIs(
  data: PayrollOverview,
  currency: string,
  t: (key: string, fallback?: string) => string
): { primary: KPIMetric; secondary: KPIMetric[] } {
  // Prefer the API's costDeltaPct; fall back to deriving from trend
  const costChange =
    data.costDeltaPct != null
      ? {
          change: `${data.costDeltaPct >= 0 ? '+' : ''}${data.costDeltaPct.toFixed(1)}%`,
          trend: (data.costDeltaPct >= 0 ? 'up' : 'down') as 'up' | 'down',
        }
      : getChangeFromTrend(data.trend, 'cost');

  const primary: KPIMetric = {
    id: 'primary-payroll',
    title: t('payroll.cost', 'Payroll Cost'),
    value: formatCurrency(data.currentCost, currency),
    ...(costChange ?? {}),
    icon: 'DollarSign',
    color: 'green',
    category: 'payroll',
    description: t(
      'payroll.cost_tooltip',
      'Total payroll expenditure for the selected period'
    ),
  };

  const pendingClaims = data.claims?.pending ?? 0;

  const secondary: KPIMetric[] = [
    {
      id: 'pay-claims',
      title: t('payroll.claims', 'Pending Claims'),
      value: pendingClaims.toString(),
      icon: 'FileText',
      color: 'orange',
      category: 'payroll',
      description: t(
        'payroll.claims_tooltip',
        'Number of expense claims awaiting approval'
      ),
    },
    {
      id: 'pay-benefits',
      title: t('payroll.benefits', 'Benefits Cost'),
      value: formatCurrency(data.benefitsCost, currency),
      icon: 'Heart',
      color: 'blue',
      category: 'payroll',
      description: t(
        'payroll.benefits_tooltip',
        'Total cost of employee benefits for the period'
      ),
    },
  ];

  return { primary, secondary };
}

// =============================================================================
// Employees Mapping
// =============================================================================

export function mapEmployeesToKPIs(
  data: EmployeesOverview,
  t: (key: string, fallback?: string) => string
): { primary: KPIMetric; secondary: KPIMetric[] } {
  const netChange = (data.hires ?? 0) - (data.exits ?? 0);
  const headcountChange = getChangeFromTrend(data.trend, 'headcount');

  const primary: KPIMetric = {
    id: 'primary-employees',
    title: t('employees.headcount', 'Headcount'),
    value: (data.headcount ?? 0).toString(),
    ...(headcountChange ?? {}),
    icon: 'Users',
    color: 'blue',
    category: 'employee',
    description: t(
      'employees.headcount_tooltip',
      'Total number of active employees'
    ),
  };

  const secondary: KPIMetric[] = [
    {
      id: 'emp-movement',
      title: t('employees.hires_exits', 'Hires / Exits'),
      value: `${data.hires ?? 0} / ${data.exits ?? 0}`,
      // Net delta is informational, not a vs-previous comparison
      change: t('employees.net', 'net') + ` ${netChange >= 0 ? '+' : ''}${netChange}`,
      isNeutralChange: true,
      icon: 'UserPlus',
      color: 'green',
      category: 'employee',
      description: t(
        'employees.hires_exits_tooltip',
        'New hires vs departures for the period'
      ),
    },
    {
      id: 'emp-turnover',
      title: t('employees.turnover', 'Turnover %'),
      value: formatPercentage(data.turnover),
      icon: 'RefreshCw',
      color: 'orange',
      category: 'employee',
      description: t(
        'employees.turnover_tooltip',
        'Employee turnover rate for the period'
      ),
    },
  ];

  return { primary, secondary };
}

// =============================================================================
// Projects Mapping
// =============================================================================

export function mapProjectsToKPIs(
  data: ProjectsOverview,
  t: (key: string, fallback?: string) => string
): { primary: KPIMetric; secondary: KPIMetric[] } {
  const primary: KPIMetric = {
    id: 'primary-projects',
    title: t('projects.active', 'Active Projects'),
    value: (data.active ?? 0).toString(),
    icon: 'Briefcase',
    color: 'purple',
    category: 'project',
    description: t(
      'projects.active_tooltip',
      'Number of currently active projects'
    ),
  };

  const secondary: KPIMetric[] = [
    {
      id: 'proj-ontrack',
      title: t('projects.on_track', 'On Track %'),
      value: formatPercentage(data.onTrackPct),
      icon: 'Target',
      color: 'green',
      category: 'project',
      description: t(
        'projects.on_track_tooltip',
        'Percentage of projects progressing on schedule'
      ),
    },
    {
      id: 'proj-overdue',
      title: t('projects.overdue', 'Overdue Tasks'),
      value: (data.overdue ?? 0).toString(),
      icon: 'AlertTriangle',
      color: 'red',
      category: 'project',
      description: t(
        'projects.overdue_tooltip',
        'Number of tasks past their due date'
      ),
    },
  ];

  return { primary, secondary };
}

// =============================================================================
// Holidays Mapping
// =============================================================================

/**
 * Map upcoming holidays API response to HolidayCard props.
 * Calculates daysUntil from the holiday date.
 */
export function mapHolidaysToCards(
  holidays: Array<{ name: string; date: string }>
): HolidayCardData[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return holidays.map((holiday, index) => {
    const holidayDate = new Date(holiday.date);
    holidayDate.setHours(0, 0, 0, 0);
    const diffTime = holidayDate.getTime() - today.getTime();
    const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      id: `holiday-${index}`,
      name: holiday.name,
      date: holiday.date,
      daysUntil: Math.max(0, daysUntil),
    };
  });
}
